import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import {
  addDays,
  formatDayShort,
  readableInk,
  startOfWeek,
  todayIn,
  type BoardCell,
  type BoardChip,
  type PlanMoveResult,
  type StationKey,
} from '@jobshop/shared';
import {
  useBoard,
  useMovePlan,
  useReplanAll,
  usePinBooking,
  useSchedulableStations,
  useShopTimeZone,
  useUndoPlanMove,
} from '../../api/hooks';
import { useSession } from '../../auth/useUser';
import { Card, CardHeader, ErrorNote, Spinner, WarningChip } from '../../components/ui';

/**
 * Dragging a chip never writes a booking — it moves one constraint and re-runs the
 * engine, so what you get back is always a plan the scheduler would actually produce.
 * The gesture is one sentence: *everything from the chip you grabbed onward moves,
 * everything before it stays.*
 */
interface Dnd {
  active: BoardChip | null;
  enabled: boolean;
  pending: boolean;
  start: (chip: BoardChip) => void;
  end: () => void;
  drop: (stationKey: StationKey, date: string) => void;
}

/**
 * The fortnight board: stations down, days across. Replaces the 14 hardcoded
 * `#fgday01…14` divs — the window is a parameter now, so paging is real.
 */
export function BoardPage() {
  const tz = useShopTimeZone();
  const [from, setFrom] = useState(() => startOfWeek(todayIn(tz)));
  const [days, setDays] = useState(14);
  const board = useBoard(from, days);
  // The board is the plan, so it shows only stations the plan can reach — a scan-only
  // one has no capacity and never gets a cell, and an empty row for it is just noise.
  const stationList = useSchedulableStations();
  const replan = useReplanAll();
  const pin = usePinBooking();
  const session = useSession();
  const movePlan = useMovePlan();
  const undoMove = useUndoPlanMove();
  const today = todayIn(tz);

  const [dragging, setDragging] = useState<BoardChip | null>(null);
  /** What the last drag did — the honest part of the feature. */
  const [note, setNote] = useState<(PlanMoveResult & { batchId: string; jobId: string }) | null>(
    null,
  );

  const dnd: Dnd = {
    active: dragging,
    enabled: session.can('planner'),
    pending: movePlan.isPending,
    start: setDragging,
    end: () => setDragging(null),
    drop: (stationKey, date) => {
      const chip = dragging;
      setDragging(null);
      if (!chip) return;
      movePlan.mutate(
        // The id, not just the day: a station can carry two chips of one batch on one
        // date — a pinned one and hours a re-plan put beside it — and the server has no
        // other way to tell which was grabbed.
        {
          batchId: chip.batchId,
          stationKey,
          fromDate: chip.date,
          bookingId: chip._id,
          toDate: date,
        },
        {
          onSuccess: (res) => setNote({ ...res, batchId: chip.batchId, jobId: chip.jobId }),
        },
      );
    },
  };

  const cellByKey = useMemo(() => {
    const map = new Map<string, BoardCell>();
    for (const cell of board.data?.cells ?? []) map.set(`${cell.stationKey}|${cell.date}`, cell);
    return map;
  }, [board.data]);

  const dayList = board.data?.days ?? [];

  return (
    <div className="space-y-3">
      <Card>
        <CardHeader
          title="Board"
          subtitle={
            board.data
              ? `${formatDayShort(board.data.from)} → ${formatDayShort(
                  addDays(board.data.from, dayList.length - 1),
                )}`
              : undefined
          }
          actions={
            <>
              <div className="flex items-center gap-1">
                {/*
                  A window of its own, on purpose: the month view answers a different
                  question from the board and is meant to sit open on a second screen
                  beside it, not to replace it.
                */}
                <button
                  className="btn-secondary bg-ink-100 hover:bg-ink-200"
                  onClick={() =>
                    window.open('/calendar', 'jobshop-calendar', 'popup=yes,width=1400,height=900')
                  }
                  title="Opens this month and the next in a separate window — a read-only overview of which jobs are in the shop."
                >
                  Months
                </button>
                <button className="btn-secondary" onClick={() => setFrom(addDays(from, -28))}>
                  ‹‹ 4w
                </button>
                <button className="btn-secondary" onClick={() => setFrom(addDays(from, -7))}>
                  ‹ 1w
                </button>
                <button className="btn-secondary" onClick={() => setFrom(startOfWeek(todayIn(tz)))}>
                  Today
                </button>
                <button className="btn-secondary" onClick={() => setFrom(addDays(from, 7))}>
                  1w ›
                </button>
                <button className="btn-secondary" onClick={() => setFrom(addDays(from, 28))}>
                  4w ››
                </button>
              </div>
              <select
                className="field w-auto"
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
                aria-label="Window length"
              >
                <option value={7}>1 week</option>
                <option value={14}>2 weeks</option>
                <option value={28}>4 weeks</option>
              </select>
              <button
                className="btn-primary"
                disabled={replan.isPending}
                onClick={() => replan.mutate()}
                title="Re-plans every open job in priority order, then by delivery date — the only one of the three that can move a job to make room for a more urgent one. Pinned bookings stay put."
              >
                {replan.isPending ? 'Re-planning…' : 'Re-plan all jobs'}
              </button>
            </>
          }
        />

        {note ? (
          <MoveNote
            note={note}
            onDismiss={() => setNote(null)}
            onUndo={() => {
              undoMove.mutate(note.batchId);
              setNote(null);
            }}
            undoing={undoMove.isPending}
          />
        ) : null}

        {board.isLoading ? <Spinner /> : null}
        {board.error || movePlan.error || undoMove.error ? (
          <div className="p-4">
            <ErrorNote error={board.error ?? movePlan.error ?? undoMove.error} />
          </div>
        ) : null}

        {board.data ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1040px] border-collapse text-sm">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 w-48 border-b border-ink-200 bg-white px-2 py-2 text-left font-semibold">
                    Station
                  </th>
                  {dayList.map((day) => (
                    <th
                      key={day.date}
                      className={clsx(
                        // The dates stay compact: they are short, already legible, and
                        // widening fourteen of them is what pushes the fortnight off screen.
                        'border-b border-l border-ink-200 px-1 py-2 text-center text-xs font-medium',
                        !day.isWorkday && 'bg-ink-100 text-ink-400',
                        day.date === today && 'bg-amber-50',
                      )}
                    >
                      {formatDayShort(day.date)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {stationList.map((station) => (
                  <tr key={station.key}>
                    <th
                      scope="row"
                      className="sticky left-0 z-10 border-b border-ink-200 bg-white px-2 py-1.5 text-left font-medium"
                    >
                      <span className="flex items-center gap-2">
                        <span
                          className="inline-block h-3 w-3 shrink-0 rounded-sm"
                          style={{ backgroundColor: station.color }}
                          aria-hidden
                        />
                        {station.name}
                        {station.machineCapacityHours !== undefined ? (
                          <span
                            className="text-[0.6875rem] font-normal text-ink-400"
                            title="Capacity is capped by the machine, not by headcount"
                          >
                            {station.machineCapacityHours}h cap
                          </span>
                        ) : null}
                      </span>
                    </th>
                    {dayList.map((day) => {
                      const cell = cellByKey.get(`${station.key}|${day.date}`);
                      // A booking cannot change station — that would be a routing edit —
                      // so the whole row, and only that row, takes the drop.
                      const droppable =
                        dnd.active?.stationKey === station.key && dnd.active.date !== day.date;
                      return (
                        <td
                          key={day.date}
                          onDragOver={droppable ? (e) => e.preventDefault() : undefined}
                          onDrop={
                            droppable
                              ? (e) => {
                                  e.preventDefault();
                                  dnd.drop(station.key, day.date);
                                }
                              : undefined
                          }
                          className={clsx(
                            'border-b border-l border-ink-200 p-0.5 align-top',
                            !day.isWorkday && 'bg-ink-100',
                            day.date === today && 'bg-amber-50/60',
                            droppable &&
                              'outline-dashed outline-1 -outline-offset-1 outline-ink-400',
                          )}
                        >
                          <DayCell cell={cell} color={station.color} onPin={pin.mutate} dnd={dnd} />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Card>

      <div className="space-y-1 text-xs text-ink-500">
        <p>
          Click a chip to pin or unpin it. A pinned booking is treated as fixed load by every
          scheduling operation, including <em>Re-plan all jobs</em>.
        </p>
        <p>
          Drag a chip along its row to move that station later. Everything from the chip you
          grabbed onward moves — grab the first station to move the whole job, or grab{' '}
          <em>Delivery</em> to hold the cabinets and push only the delivery out. Dragging back
          takes a move away again; nothing can be dragged earlier than the plan already puts it.
        </p>
      </div>
    </div>
  );
}

function DayCell({
  cell,
  color,
  onPin,
  dnd,
}: {
  cell: BoardCell | undefined;
  color: string;
  onPin: (input: { id: string; pinned: boolean }) => void;
  dnd: Dnd;
}) {
  if (!cell || (cell.chips.length === 0 && cell.capacityHours === 0)) {
    return <div className="min-h-[26px]" />;
  }
  const free = cell.capacityHours - cell.bookedHours;
  return (
    <div className="min-h-[26px] space-y-0.5">
      {cell.chips.map((chip) => (
        <Chip key={chip._id} chip={chip} color={color} onPin={onPin} dnd={dnd} />
      ))}
      {cell.capacityHours > 0 ? (
        <div
          className={clsx(
            'px-1 text-[0.6875rem] leading-tight',
            free < 0 ? 'font-semibold text-red-600' : 'text-ink-400',
          )}
          title={`${cell.bookedHours}h booked of ${cell.capacityHours}h capacity`}
        >
          {cell.bookedHours}/{cell.capacityHours}h
        </div>
      ) : null}
    </div>
  );
}

function Chip({
  chip,
  color,
  onPin,
  dnd,
}: {
  chip: BoardChip;
  color: string;
  onPin: (input: { id: string; pinned: boolean }) => void;
  dnd: Dnd;
}) {
  // A pinned booking is fixed by definition, so it is not draggable — unpin it first.
  // That keeps "pinned" meaning one thing rather than two.
  const draggable = dnd.enabled && !chip.pinned && !dnd.pending;
  return (
    <div
      draggable={draggable}
      onDragStart={
        draggable
          ? (e) => {
              e.dataTransfer.effectAllowed = 'move';
              // Firefox starts no drag at all without payload on the transfer.
              e.dataTransfer.setData('text/plain', chip._id);
              dnd.start(chip);
            }
          : undefined
      }
      onDragEnd={dnd.end}
      className={clsx(
        // Capped so one long job name cannot widen a whole day column and push the
        // fortnight off screen — the label truncates instead, and its colour still
        // says which job it is.
        'flex max-w-[11rem] items-center gap-1 rounded px-1 py-0.5 text-[0.8125rem] leading-tight',
        chip.pinned && 'ring-1 ring-inset ring-ink-900',
        draggable && 'cursor-grab active:cursor-grabbing',
        dnd.active?._id === chip._id && 'opacity-50',
      )}
      style={{ backgroundColor: color }}
    >
      <button
        type="button"
        onClick={() => onPin({ id: chip._id, pinned: !chip.pinned })}
        aria-label={chip.pinned ? `Unpin ${chip.label}` : `Pin ${chip.label}`}
        title={
          chip.pinned
            ? 'Pinned — every scheduling operation treats this as fixed. Click to unpin.'
            : 'Click to pin this booking in place.'
        }
      >
        {chip.pinned ? '📌' : '○'}
      </button>
      {/*
        Two colours at once, on purpose: the chip is the station's, the marker behind
        the name is the job's. The station never changes for a row, so the job colour
        is the one doing the work — following one kitchen across the board.
      */}
      <Link
        to={`/jobs/${chip.jobId}`}
        draggable={false}
        className="min-w-0 flex-1 truncate"
        title={chip.label}
      >
        <span
          className="rounded-sm px-1 py-px hover:underline"
          style={{ backgroundColor: chip.jobColor, color: readableInk(chip.jobColor) }}
        >
          {chip.label}
        </span>
      </Link>
      <span className="shrink-0 opacity-70">{chip.hours}h</span>
    </div>
  );
}

/**
 * What the drag actually did. Half the point of the feature: a plan that moved says so
 * and offers to take it back, and a plan that could not move says why rather than
 * silently snapping the chip home.
 */
function MoveNote({
  note,
  onUndo,
  onDismiss,
  undoing,
}: {
  note: PlanMoveResult & { batchId: string; jobId: string };
  onUndo: () => void;
  onDismiss: () => void;
  undoing: boolean;
}) {
  const warnings = note.result?.warnings ?? [];
  return (
    <div
      className={clsx(
        'flex flex-wrap items-center gap-x-3 gap-y-1 border-b px-4 py-2 text-xs',
        note.moved ? 'border-ink-200 bg-ink-50' : 'border-amber-200 bg-amber-50',
      )}
    >
      {note.moved ? (
        <span className="text-ink-700">
          Moved to <strong>{formatDayShort(note.to ?? note.from)}</strong>
          {note.heldDays > 0
            ? ` — ${note.heldDays} earlier day${note.heldDays === 1 ? '' : 's'} pinned in place`
            : ''}
          {note.result?.finishDate ? ` · job now finishes ${formatDayShort(note.result.finishDate)}` : ''}
        </span>
      ) : (
        <span className="text-amber-900">{note.reason}</span>
      )}

      {warnings.map((w, i) => (
        <WarningChip key={i} warning={w} />
      ))}

      {!note.moved ? (
        <Link to={`/jobs/${note.jobId}`} className="underline">
          Open the job to raise its priority
        </Link>
      ) : null}

      <span className="ml-auto flex items-center gap-2">
        {note.moved ? (
          <button className="btn-secondary" onClick={onUndo} disabled={undoing}>
            {undoing ? 'Undoing…' : 'Undo'}
          </button>
        ) : null}
        <button className="btn-secondary" onClick={onDismiss}>
          Dismiss
        </button>
      </span>
    </div>
  );
}
